import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Table, Badge, Spinner, Button, Modal, Alert, Form } from 'react-bootstrap';
import {
  useGetEntrepreneurUsageChargesQuery,
  useChargeEntrepreneurUsageMutation,
} from '../../app/api';
import { brand, usd, cardStyle, headStyle, EntPage, FilterBar, filterInputStyle, Fact } from './common';

/* Manual monthly usage billing. Lists every ACTIVE Claude firm's usage for
 * each recently closed month against what's already been PAID, and charges
 * one row's outstanding balance only after a person confirms the amount.
 * The backend re-checks the balance inside the billing lock and refuses to
 * charge if it no longer matches what was confirmed here. */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const monthLabel = (row) => `${MONTH_NAMES[row.month - 1]} ${row.year}`;

const cell = { padding: '0.5rem 0.9rem', verticalAlign: 'middle' };
const num = { ...cell, textAlign: 'right' };

const SKIP_REASON = {
  fully_billed: 'Nothing is owed for this month - it is already fully billed.',
  no_payment_method: 'This firm has no card on file.',
  not_active: 'This firm is not ACTIVE for Claude.',
  no_account: 'This firm has no billing account.',
  concurrent_run_in_progress: 'Another billing run for this firm and month is in progress. Try again in a minute.',
};

function RowStatus({ row }) {
  if (row.usage_error) return <Badge bg="danger">Usage unavailable</Badge>;
  if (row.outstanding_cents > 0) {
    return (
      <>
        <Badge bg="warning" text="dark">Outstanding</Badge>
        {row.last_status === 'FAILED' && <div style={{ fontSize: '0.68rem', color: brand.red, marginTop: 2 }}>Last attempt failed</div>}
      </>
    );
  }
  if (row.paid_usage_cents > 0) return <Badge bg="success">Paid</Badge>;
  return <span style={{ color: brand.charcoal, fontSize: '0.78rem' }}>No usage</span>;
}
RowStatus.propTypes = { row: PropTypes.object.isRequired };

// Mirrors stripe_gateway.gross_up so the combined total shown before
// confirming matches what the server charges. The server is still the
// authority: it recomputes, and refuses if any month's balance moved.
function grossUp(cents, feePct, feeFixed) {
  if (cents <= 0) return { fee: 0, total: 0 };
  const total = Math.round((cents + feeFixed) / (1 - feePct));
  return { fee: total - cents, total };
}

// Confirms and charges one or more of a firm's outstanding months as ONE
// Stripe charge (one fee).
function ChargeModal({ firm, rows, fee, minChargeCents, onClose, onDone }) {
  const [charge, { isLoading }] = useChargeEntrepreneurUsageMutation();
  const [result, setResult] = useState(null); // { variant, text }

  const multi = rows.length > 1;
  const usage = rows.reduce((a, r) => a + r.outstanding_cents, 0);
  const { fee: feeCents, total } = multi
    ? grossUp(usage, fee.pct, fee.fixed)
    : { fee: rows[0].fee_cents, total: rows[0].charge_total_cents };
  const settling = rows.some(r => r.settling);
  const belowMin = total > 0 && total < minChargeCents;
  const isTopup = !multi && rows[0].paid_usage_cents > 0;
  const stripeLabel = `PCA B2B ${isTopup ? 'Additional Claude usage' : 'Claude usage'} - ${firm.firm_name} - ${[...rows].reverse().map(monthLabel).join(', ')}`;

  const submit = async () => {
    try {
      const res = await charge({
        firm_id: firm.firm_id,
        months: rows.map(r => ({ month: r.period, expected_remaining_cents: r.outstanding_cents })),
      }).unwrap();
      if (res.result === 'paid') {
        setResult({ variant: 'success', text: `Charged ${usd(res.total_cents)} to ${firm.firm_name}'s card${multi ? ` for ${res.periods.length} months in one charge` : ''}.` });
      } else if (res.result === 'failed') {
        setResult({ variant: 'danger', text: `The charge failed: ${res.error || 'declined'}. The balance is still outstanding${firm.billing_contact_email ? ` and ${firm.billing_contact_email} was emailed` : ''}.` });
      } else if (res.result === 'void') {
        setResult({ variant: 'secondary', text: 'The balance is below the minimum charge, so it was recorded as VOID and not charged.' });
      } else if (res.reason === 'amount_changed') {
        const now = Object.entries(res.remaining_by_month || {}).map(([m, c]) => `${m}: ${usd(c)}`).join(', ');
        setResult({ variant: 'warning', text: `Usage changed since this page loaded${now ? ` (now ${now})` : ''}. Nothing was charged. Close this and review the refreshed amounts.` });
      } else {
        setResult({ variant: 'secondary', text: SKIP_REASON[res.reason] || `Not charged (${res.reason || res.result}).` });
      }
      onDone();
    } catch (err) {
      setResult({ variant: 'danger', text: err?.data?.detail || 'The charge request failed. Nothing was charged.' });
    }
  };

  const line = (label, value, strong) => (
    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: `1px solid ${brand.softGray}`, fontWeight: strong ? 700 : 400, color: strong ? brand.deepBlue : brand.charcoal }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );

  return (
    <Modal show onHide={isLoading ? undefined : onClose} centered>
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title style={{ color: brand.deepBlue, fontSize: '1.1rem' }}>
          Charge {firm.firm_name} for {multi ? `${rows.length} months` : monthLabel(rows[0])}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: '0.88rem' }}>
        {multi ? (
          <>
            {[...rows].reverse().map(r => line(`${monthLabel(r)}${r.paid_usage_cents > 0 ? ' (balance after earlier payment)' : ''}`, usd(r.outstanding_cents)))}
            {line('Outstanding usage', usd(usage))}
          </>
        ) : (
          <>
            {line('Claude usage for the month', usd(rows[0].usage_cents))}
            {line('Already paid', usd(rows[0].paid_usage_cents))}
            {line('Outstanding usage', usd(usage))}
          </>
        )}
        {line('Card processing fee', usd(feeCents))}
        {line('Total to charge', usd(total), true)}

        <div style={{ color: brand.charcoal, fontSize: '0.8rem', marginTop: '0.75rem' }}>
          {multi ? 'One charge to the firm\'s card on file covering every month above.' : 'Charged to the firm\'s card on file.'}
          {firm.billing_contact_email ? <> A receipt is emailed to <strong>{firm.billing_contact_email}</strong>.</> : ' The firm has no billing contact, so no receipt email is sent.'}
          {' '}In Stripe it shows as &quot;{stripeLabel}&quot;.
        </div>

        {settling && !result && (
          <Alert variant="warning" className="mt-3 mb-0" style={{ fontSize: '0.8rem' }}>
            A month here closed only a few days ago. Anthropic&apos;s usage data can lag about 3 days, so its total may still grow. Any later usage can be charged from this page as an additional charge.
          </Alert>
        )}
        {belowMin && !result && (
          <Alert variant="secondary" className="mt-3 mb-0" style={{ fontSize: '0.8rem' }}>
            This is below the minimum charge, so it will be recorded as VOID and not charged.
          </Alert>
        )}
        {result && <Alert variant={result.variant} className="mt-3 mb-0" style={{ fontSize: '0.85rem' }}>{result.text}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <Button variant="primary" onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
            <Button variant="success" onClick={submit} disabled={isLoading}>
              {isLoading ? <><Spinner size="sm" className="me-1" />Charging…</> : `Charge ${usd(total)}`}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
ChargeModal.propTypes = {
  firm: PropTypes.object.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  fee: PropTypes.shape({ pct: PropTypes.number, fixed: PropTypes.number }).isRequired,
  minChargeCents: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default function ChargeUsage() {
  const [months, setMonths] = useState(6);
  const { data, isLoading, isFetching, error, refetch } = useGetEntrepreneurUsageChargesQuery({ months });
  const [search, setSearch] = useState('');
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [expanded, setExpanded] = useState({}); // `${firm_id}-${period}` -> bool
  const [target, setTarget] = useState(null); // { firm, rows }

  // Rows with no usage and nothing paid are noise; always hide them.
  const firms = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.firms || [])
      .filter(f => !q || f.firm_name.toLowerCase().includes(q))
      .map(f => ({
        ...f,
        rows: f.months.filter(r => {
          if (outstandingOnly) return r.outstanding_cents > 0 || r.usage_error;
          return r.usage_error || r.usage_cents > 0 || r.paid_usage_cents > 0;
        }),
      }))
      .filter(f => f.rows.length > 0);
  }, [data, search, outstandingOnly]);

  const totals = useMemo(() => {
    let outstanding = 0; let toCharge = 0; let rows = 0; const firmIds = new Set();
    (data?.firms || []).forEach(f => f.months.forEach(r => {
      if (r.outstanding_cents > 0) {
        outstanding += r.outstanding_cents; toCharge += r.charge_total_cents; rows += 1; firmIds.add(f.firm_id);
      }
    }));
    return { outstanding, toCharge, rows, firms: firmIds.size };
  }, [data]);

  const toggle = (key) => setExpanded(e => ({ ...e, [key]: !e[key] }));

  return (
    <EntPage
      title="Charge Usage"
      subtitle="Monthly Claude usage by firm against what has been paid. Review a month, then charge the outstanding balance to the firm's card."
    >
      {data && !data.analytics_configured && (
        <Alert variant="danger" style={{ fontSize: '0.85rem' }}>
          The Anthropic Analytics key is not configured on this server, so usage shows as $0. Do not charge from this page until it is.
        </Alert>
      )}
      {error && <Alert variant="danger" style={{ fontSize: '0.85rem' }}>{error?.data?.detail || 'Could not load usage charges.'}</Alert>}

      {data && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <Fact label="Outstanding usage" value={usd(totals.outstanding)} />
          <Fact label="To charge incl. fees" value={usd(totals.toCharge)} />
          <Fact label="Firm-months owed" value={`${totals.rows} (${totals.firms} firm${totals.firms === 1 ? '' : 's'})`} />
        </div>
      )}

      <Card style={cardStyle}>
        <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span>Usage by firm and month</span>
          {isFetching && !isLoading && <Spinner size="sm" />}
          <FilterBar search={search} onSearchChange={setSearch} placeholder="Search firm…">
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))} aria-label="Months" style={filterInputStyle}>
              <option value={3}>Last 3 months</option>
              <option value={6}>Last 6 months</option>
              <option value={12}>Last 12 months</option>
            </select>
            <Form.Check
              type="switch" id="outstanding-only" label="Outstanding only" checked={outstandingOnly}
              onChange={(e) => setOutstandingOnly(e.target.checked)} style={{ fontSize: '0.8rem', color: brand.charcoal }}
            />
            <Button size="sm" variant="outline-secondary" onClick={refetch} disabled={isFetching}>Refresh</Button>
          </FilterBar>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? <div className="text-center p-4"><Spinner size="sm" /></div> : firms.length === 0 ? (
            <div style={{ padding: '1.5rem', color: brand.charcoal, fontSize: '0.85rem' }}>
              {(data?.firms || []).length === 0 ? 'No ACTIVE Claude firms.' : outstandingOnly ? 'Nothing outstanding. Every firm is fully billed for these months.' : 'No usage in these months.'}
            </div>
          ) : (
            <Table responsive className="mb-0" style={{ fontSize: '0.85rem' }}>
              <thead><tr style={{ color: brand.charcoal }}>
                <th style={cell}>Month</th>
                <th style={{ ...cell, textAlign: 'right' }}>Usage</th>
                <th style={{ ...cell, textAlign: 'right' }}>Paid</th>
                <th style={{ ...cell, textAlign: 'right' }}>Outstanding</th>
                <th style={{ ...cell, textAlign: 'right' }}>Charge incl. fee</th>
                <th style={cell}>Status</th>
                <th style={cell} />
              </tr></thead>
              <tbody>
                {firms.map(f => {
                  // Every chargeable month in the loaded window (not just the
                  // filtered rows), for the one-charge "all outstanding" option.
                  const owed = f.charge_blocked_reason ? [] : f.months.filter(r => !r.usage_error && r.outstanding_cents > 0);
                  const owedTotal = owed.reduce((a, r) => a + r.outstanding_cents, 0);
                  return (
                  <React.Fragment key={f.firm_id}>
                    <tr style={{ background: brand.softGray }}>
                      <td colSpan={5} style={{ ...cell, fontWeight: 700, color: brand.deepBlue }}>
                        {f.firm_name}
                        {f.charge_blocked_reason && (
                          <span style={{ color: brand.red, fontWeight: 600, fontSize: '0.78rem', marginLeft: '0.75rem' }}>
                            <i className="bi bi-exclamation-triangle-fill me-1" />{f.charge_blocked_reason}
                          </span>
                        )}
                        {owed.length > 1 && (
                          <span style={{ color: brand.charcoal, fontWeight: 600, fontSize: '0.78rem', marginLeft: '0.75rem' }}>
                            {usd(owedTotal)} outstanding across {owed.length} months
                          </span>
                        )}
                      </td>
                      <td colSpan={2} style={{ ...cell, textAlign: 'right' }}>
                        {owed.length > 1 && (
                          <Button size="sm" variant="outline-success" onClick={() => setTarget({ firm: f, rows: owed })}>
                            Charge all outstanding…
                          </Button>
                        )}
                      </td>
                    </tr>
                    {f.rows.map(r => {
                      const key = `${f.firm_id}-${r.period}`;
                      const canCharge = !f.charge_blocked_reason && !r.usage_error && r.outstanding_cents > 0;
                      return (
                        <React.Fragment key={key}>
                          <tr>
                            <td style={cell}>
                              {r.member_count > 0 ? (
                                <Button variant="link" size="sm" className="p-0" onClick={() => toggle(key)} style={{ fontSize: '0.85rem', textDecoration: 'none' }}>
                                  <i className={`bi bi-chevron-${expanded[key] ? 'down' : 'right'} me-1`} />{monthLabel(r)}
                                </Button>
                              ) : monthLabel(r)}
                              {r.settling && <Badge bg="light" text="dark" className="ms-2" title="Usage data can lag about 3 days after month end">Settling</Badge>}
                            </td>
                            {r.usage_error ? (
                              <td colSpan={4} style={{ ...cell, color: brand.red, fontSize: '0.8rem' }}>{r.usage_error}</td>
                            ) : (
                              <>
                                <td style={{ ...num, color: brand.charcoal }}>{usd(r.usage_cents)}</td>
                                <td style={{ ...num, color: brand.charcoal }}>{usd(r.paid_usage_cents)}</td>
                                <td style={{ ...num, fontWeight: 700, color: r.outstanding_cents > 0 ? brand.deepBlue : brand.charcoal }}>{usd(r.outstanding_cents)}</td>
                                <td style={{ ...num, color: brand.charcoal }}>{r.outstanding_cents > 0 ? usd(r.charge_total_cents) : '-'}</td>
                              </>
                            )}
                            <td style={cell}><RowStatus row={r} /></td>
                            <td style={{ ...cell, textAlign: 'right' }}>
                              {canCharge && (
                                <Button size="sm" variant="success" onClick={() => setTarget({ firm: f, rows: [r] })}>Charge…</Button>
                              )}
                            </td>
                          </tr>
                          {expanded[key] && (r.members || []).map(m => (
                            <tr key={`${key}-${m.email}`} style={{ fontSize: '0.78rem' }}>
                              <td style={{ ...cell, paddingLeft: '2.2rem', color: brand.charcoal }}>{m.email}</td>
                              <td style={{ ...num, color: brand.charcoal }}>{usd(m.cost_cents)}</td>
                              <td colSpan={5} />
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <div style={{ color: brand.charcoal, fontSize: '0.78rem', marginTop: '0.75rem' }}>
        Usage is the firm&apos;s current active members&apos; Claude spend from Anthropic&apos;s Analytics API, the same figure the monthly billing run uses. Charging only bills what is not already paid, so a month can be charged again later if more usage lands. &quot;Charge all outstanding&quot; covers every owed month in the range shown, in one charge with one processing fee; widen the range to include older months. Every charge is recorded on Invoices and in the Actions Log.
      </div>

      {target && (
        <ChargeModal
          firm={target.firm}
          rows={target.rows}
          fee={{ pct: data.fee_percent / 100, fixed: data.fee_fixed_cents }}
          minChargeCents={data.min_charge_cents}
          onClose={() => setTarget(null)}
          onDone={refetch}
        />
      )}
    </EntPage>
  );
}
